import type { ComponentType } from 'react';
import type { IconType } from 'react-icons';
import {
  FiBriefcase,
  FiCreditCard,
  FiFileText,
  FiFolder,
  FiUsers,
} from 'react-icons/fi';

import { formatPolicyDate } from 'views/client_center/sections/Policies/policyGroups';
import { formatCurrency, formatCurrencyNumber } from '../../constants';

import { MultiYearNotice, PolicyTermValue } from './PolicyTerm';

import type { InvoicingBilling } from 'api/quote-marketplace';

type BillingComponent = ComponentType<{ billing: InvoicingBilling }>;

export type FieldConfig =
  | {
      label: string | ((billing: InvoicingBilling) => string);
      value: (billing: InvoicingBilling) => string | null;
      note?: (billing: InvoicingBilling) => string | null;
    }
  | { label: string; component: BillingComponent }
  | { label: string; pending: true };

type ValueField = Extract<FieldConfig, { value: unknown }>;

export interface FieldGroupConfig {
  title: string;
  icon: IconType;
  fields: FieldConfig[];
  footer?: BillingComponent;
}

const formatPercent = (value: string) => {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return `${num.toLocaleString('en-CA', { maximumFractionDigits: 4 })}%`;
};

const formatTax = ({
  tax_name,
  tax_rate,
  tax_applicable,
}: InvoicingBilling) => {
  if (tax_applicable === false) return 'Not applied';
  const parts = [tax_name, tax_rate && formatPercent(tax_rate)].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
};

const BROKER_FEES = [
  ['Admin', 'admin_fee'],
  ['Commission', 'commission_fee'],
  ['Agent', 'agent_fee'],
] as const;

const sumBrokerFees = (b: InvoicingBilling) => {
  const fees = BROKER_FEES.map(([, key]) => b[key]).filter(
    (v): v is string => v !== null,
  );
  if (!fees.length) return null;
  const cents = fees.reduce(
    (sum, v) => sum + Math.round(parseFloat(v) * 100),
    0,
  );
  return formatCurrencyNumber(cents / 100);
};

const brokerFeesBreakdown = (b: InvoicingBilling) =>
  BROKER_FEES.map(
    ([label, key]) => `${label} ${b[key] ? formatCurrency(b[key]) : '—'}`,
  ).join(' · ');

type StringKey = {
  [K in keyof InvoicingBilling]: InvoicingBilling[K] extends string | null
    ? K
    : never;
}[keyof InvoicingBilling];

const pending = (label: string): FieldConfig => ({ label, pending: true });

const money = (label: string, key: StringKey): ValueField => ({
  label,
  value: (b) => b[key] && formatCurrency(b[key]),
});

const date = (label: string, key: StringKey): ValueField => ({
  label,
  value: (b) => b[key] && formatPolicyDate(b[key]),
});

export const FIELD_GROUPS: FieldGroupConfig[] = [
  {
    title: 'File Assignment',
    icon: FiUsers,
    fields: [
      { label: 'Producer', value: (b) => b.producer },
      { label: 'CSR', value: (b) => b.csr },
    ],
  },
  {
    title: 'Client & Risk Details',
    icon: FiFileText,
    fields: [
      pending('Named Insured'),
      { label: 'Product Target', value: (b) => b.product_target },
      { label: 'Product Sub Type', value: (b) => b.product_sub_type },
      date('Effective Date', 'effective_date'),
      date('Expiry Date', 'expiry_date'),
      { label: 'Policy Term', component: PolicyTermValue },
      {
        label: 'Risk & Sales Tax Territory',
        value: (b) => b.sales_tax_territory ?? b.province,
      },
    ],
    footer: MultiYearNotice,
  },
  {
    title: 'Market & Application',
    icon: FiBriefcase,
    fields: [
      {
        label: 'Insurance Company on Application',
        value: (b) => b.insurer_account ?? b.carrier_name,
      },
      pending('Billing Rule'),
    ],
  },
  {
    title: 'Billing Information',
    icon: FiCreditCard,
    fields: [
      { label: 'Billing Type', value: (b) => b.invoice_account_type },
      { label: 'Invoice Method', value: (b) => b.payment_method },
      pending('Currency'),
      pending('FX Rate (USD to CAD)'),
      money('Premium (annual)', 'annual_premium'),
      money('Premium (pro-rata, policy term)', 'pro_rata_premium'),
      money('Policy Fees', 'insurer_fee'),
      pending('Issuing Fee'),
      pending('Club Marine Fee'),
      { label: 'Broker Fees', value: sumBrokerFees, note: brokerFeesBreakdown },
      { ...money('Tax', 'tax_amount'), note: formatTax },
      money('Total', 'total'),
    ],
  },
  {
    title: 'Documents',
    icon: FiFolder,
    fields: [
      {
        label: (b) =>
          !b.policy_number && b.insurer_quote_number
            ? 'Insurance Company Quote Number'
            : 'Insurance Company Policy Number',
        value: (b) => b.policy_number ?? b.insurer_quote_number,
      },
    ],
  },
];
